import {
  Button,
  CircularProgress,
  Modal,
  Paper,
  Slide,
  Typography
} from '@material-ui/core'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'

import { addCommaToPrice } from '../../helpers/numbers'
import { getDirection, getTranslate } from '../../localization'
import { Currency } from '../../types/currency'
import useStyles from './index.styles'

export interface ConfirmCancelReservationModalProps {
  isOpen: boolean
  mode: 'cancelation' | 'modification'
  data: {
    penalty: number
    payedPrice: number
    currentPrice?: number
    payBack: number
    currency: Currency
    promotionDropped?: boolean
  }
  loading?: boolean
  onClose: () => void
  onClick: () => void
}

export const ConfirmChangeReservationModal: React.FC<
  ConfirmCancelReservationModalProps
> = ({ isOpen, mode, loading, data, onClose, onClick }) => {
  const classes = useStyles()

  return (
    <Modal open={isOpen} onClose={onClose} dir={getDirection()}>
      <Slide direction="down" in={isOpen}>
        <Paper className={classes.deletePaper}>
          <div className={classes.header}>
            <WarningRoundedIcon className={classes.icon} />
            <Typography variant="h4" className={classes.title}>
              {getTranslate(
                mode === 'cancelation'
                  ? 'Cancelation Details'
                  : 'Modification Details'
              )}
            </Typography>
            <Typography variant="h5" className={classes.subTitle}>
              {getTranslate(
                mode === 'cancelation'
                  ? 'Are you sure you want to cancel this reservation?'
                  : 'Are you sure you want to modify this reservation?'
              )}
            </Typography>
          </div>
          <div className={classes.details}>
            {mode === 'modification' && data.promotionDropped && (
              <Typography variant="h6" role="alert" className={classes.redText}>
                {getTranslate(
                  'The promotion no longer applies to the modified stay. The new price is without it.'
                )}
              </Typography>
            )}
            <div className={classes.row}>
              <Typography variant="h6">
                {getTranslate('Payed Price')}
              </Typography>
              <Typography variant="h6">{`${addCommaToPrice(
                data.payedPrice
              )} ${getTranslate(data.currency || '')}`}</Typography>
            </div>
            <div className={classes.row}>
              <Typography variant="h6">{getTranslate('Penalty')}</Typography>
              <Typography
                variant="h6"
                className={classes.redText}>{`${addCommaToPrice(
                data.penalty
              )} ${getTranslate(data.currency || '')}`}</Typography>
            </div>
            {data.currentPrice && (
              <div className={classes.row}>
                <Typography variant="h6">
                  {getTranslate('Current Price')}
                </Typography>
                <Typography
                  variant="h6"
                  className={classes.redText}>{`${addCommaToPrice(
                  data.currentPrice
                )} ${getTranslate(data.currency || '')}`}</Typography>
              </div>
            )}
            <div className={classes.row}>
              <Typography variant="h6">
                {getTranslate(data.payBack > 0 ? 'Pay Back' : 'Pay ')}
              </Typography>
              <Typography variant="h6">
                {`${addCommaToPrice(Math.abs(data.payBack))} ${getTranslate(
                  data.currency || ''
                )}`}
              </Typography>
            </div>
          </div>
          <div className={classes.buttonsContainer}>
            <Button
              variant="text"
              className={classes.confirmBtn}
              onClick={onClick}
              disabled={loading}
              color="primary">
              <Typography variant="h5" className={classes.changeText}>
                {getTranslate('Yes')}
              </Typography>
              {loading && (
                <CircularProgress
                  className={classes.circularProgress}
                  size={14}
                />
              )}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onClose}
              disabled={loading}>
              <Typography variant="h5" className={classes.cancelText}>
                {getTranslate('No')}
              </Typography>
            </Button>
          </div>
        </Paper>
      </Slide>
    </Modal>
  )
}
